/**
 * RevenueSplitReport (FEAT-ANALYTICS, Monetization) — in-window revenue & buyers
 * split by NEW vs RETURNING users (classed by first-ever event). Ties the
 * revenue story to retention: are you earning from fresh acquisition or your
 * installed base, and which is worth more per buyer?
 */
import React from 'react';
import { Stack, SimpleGrid, Box, Flex, Text } from '@chakra-ui/react';
import { useReport } from './api';
import { StatCard, StatGrid, Panel, TrendChart, ReportFrame, fmtInt, fmtPct } from './components';
import { fmtMoney } from './txnFormat';
import { CurrencyBreakdown } from './CurrencyBreakdown';

const catsOf = (b = []) => b.map((x) => String(x || '').replace(/ 00:00:00$/, '').slice(5));
const NEW = '#2563eb';
const RET = '#16a34a';

function ShareBar({ newPct = 0, retPct = 0 }) {
  return (
    <Box>
      <Flex h="26px" borderRadius="6px" overflow="hidden">
        <Flex bg={NEW} w={`${newPct}%`} align="center" justify="center" minW={newPct > 8 ? 'auto' : '0'}>
          {newPct > 8 && <Text color="white" fontSize="11px" fontWeight="700">{newPct}%</Text>}
        </Flex>
        <Flex bg={RET} w={`${retPct}%`} align="center" justify="center">
          {retPct > 8 && <Text color="white" fontSize="11px" fontWeight="700">{retPct}%</Text>}
        </Flex>
      </Flex>
      <Flex justify="space-between" mt={2} fontSize="xs" color="gray.600">
        <Flex align="center" gap={1}><Box w="8px" h="8px" borderRadius="full" bg={NEW} />New users</Flex>
        <Flex align="center" gap={1}><Box w="8px" h="8px" borderRadius="full" bg={RET} />Returning users</Flex>
      </Flex>
    </Box>
  );
}

function SegCard({ title, seg, color, cur }) {
  return (
    <Box bg="white" borderWidth="1px" borderColor="#ebedf0" borderRadius="14px" p={4} borderTop="3px solid" borderTopColor={color}>
      <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.05em" mb={2}>{title}</Text>
      <Text fontSize="26px" fontWeight="700" color="gray.900" lineHeight="1" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(seg.revenue, cur)}</Text>
      <SimpleGrid columns={3} spacing={2} mt={4}>
        <Box><Text fontSize="10px" color="gray.500">Buyers</Text><Text fontSize="15px" fontWeight="600">{fmtInt(seg.buyers)}</Text></Box>
        <Box><Text fontSize="10px" color="gray.500">Avg order</Text><Text fontSize="15px" fontWeight="600">{fmtMoney(seg.avg_order_value, cur)}</Text></Box>
        <Box><Text fontSize="10px" color="gray.500">Rev / buyer</Text><Text fontSize="15px" fontWeight="600">{fmtMoney(seg.revenue_per_buyer, cur)}</Text></Box>
      </SimpleGrid>
    </Box>
  );
}

export function RevenueSplitReport({ params }) {
  const { data, loading, error } = useReport('revenue-split', params);
  const t = data?.trend || {};
  // Per-currency new/returning split (DA-BUG-0455): never blend across currencies.
  const byCur = data?.by_currency || [];
  const multi = byCur.length > 1;
  const cur = byCur.length === 1 ? byCur[0].currency : '';
  const money = (v) => (multi ? 'By currency ↓' : fmtMoney(v, cur));
  const curRows = byCur.map((r) => ({
    currency: r.currency,
    total_revenue: r.total_revenue,
    new_revenue: r.new?.revenue || 0,
    returning_revenue: r.returning?.revenue || 0,
  }));
  return (
    <ReportFrame loading={loading} error={error}>
      {data && (
        <Stack spacing={4}>
          <StatGrid columns={{ base: 2, md: 4 }}>
            <StatCard label="Total revenue" value={money(data.total_revenue)} />
            <StatCard label="New-user revenue" value={money(data.new.revenue)} help={fmtPct(data.new_revenue_share) + ' of total'} />
            <StatCard label="Returning revenue" value={money(data.returning.revenue)} help={fmtPct(data.returning_revenue_share) + ' of total'} />
            <StatCard label="Returning rev/buyer" value={money(data.returning.revenue_per_buyer)} help={multi ? undefined : `new: ${fmtMoney(data.new.revenue_per_buyer, cur)}`} />
          </StatGrid>

          {multi ? (
            <CurrencyBreakdown
              title="Revenue by currency — new vs returning"
              rows={curRows}
              cols={[
                { key: 'total_revenue', label: 'Total', money: true },
                { key: 'new_revenue', label: 'New', money: true },
                { key: 'returning_revenue', label: 'Returning', money: true },
              ]}
            />
          ) : (
            <>
              <Panel title="Revenue share — new vs returning">
                <ShareBar newPct={data.new_revenue_share} retPct={data.returning_revenue_share} />
              </Panel>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <SegCard title="New users" seg={data.new} color={NEW} cur={cur} />
                <SegCard title="Returning users" seg={data.returning} color={RET} cur={cur} />
              </SimpleGrid>

              <Panel title="Revenue over time — new vs returning">
                <TrendChart
                  categories={catsOf(t.buckets)}
                  series={[{ name: 'New', data: t.new || [] }, { name: 'Returning', data: t.returning || [] }]}
                />
              </Panel>
            </>
          )}
        </Stack>
      )}
    </ReportFrame>
  );
}

export default RevenueSplitReport;
